import type { RefObject } from "react";

import { shouldCorrectLanding } from "./_active-section";
import { markerOffset } from "./_tour-nav.client";
import type { TourTab } from "./_section-order";

/**
 * A backstop, not the primary defense — see `forceSectionsLaidOut` below for
 * that. This bounds the rare residual correction (sub-pixel rounding, or a
 * genuinely dynamic widget resizing after it loads), together with
 * `shouldCorrectLanding`'s requirement that each pass actually get closer.
 *
 * This USED to be the only defense, and its history is the reason it no
 * longer is: it went 1 → 3 → (briefly) 8, each bump chasing a worse measured
 * case than the last — 68px short, then 1,751px, then over 2,200px on a page
 * load — because the number it was bounding (how far a `content-visibility:
 * auto` section's ESTIMATED height can be from its real one) has no ceiling
 * of its own. However large this constant is, a page with one more
 * unresolved section than it budgeted for still lands short, and
 * `pickActiveSection` then — correctly, by its own rules — reads the PREVIOUS
 * section as current, because the target genuinely had not reached the
 * marker yet. The tab bar highlighted "What's included" after a deep link to
 * "Map"; before that, "Itinerary" after a click on "Reviews". Resolving the
 * estimates before the first measurement removes the unknown this was
 * chasing; this constant now only needs to cover what's left over. */
const MAX_LANDING_CORRECTIONS = 3;

/** Used only where `scrollend` is unavailable (Safari before 26). Long enough
 *  to cover a full-page smooth scroll, and it is a backstop rather than the
 *  primary signal, so being generous costs nothing. */
const SCROLL_SETTLE_FALLBACK_MS = 1200;

/**
 * Run `done` once the page has actually stopped scrolling.
 *
 * `scrollend` and not a timer: a smooth scroll's duration is the browser's
 * business and scales with distance, so any fixed wait is either too short on a
 * long page or a stall on a short one. It also fires when the reader interrupts
 * the animation by scrolling themselves, which a timer cannot notice.
 *
 * Deliberately not a `scroll` listener with a debounce — this app holds a hard
 * INP budget and a per-frame handler is the classic way to blow it.
 *
 * Returns a canceller, so a second click supersedes the first rather than
 * leaving two corrections racing each other.
 */
function whenScrollSettles(done: () => void): () => void {
  // A frame between the scroll ending and the measurement. `scrollend` fires as
  // soon as the scroll position stops moving, which on this page is BEFORE the
  // layout it revealed has finished: the sections it passed are
  // `content-visibility: auto` and swap their estimated height for their real
  // one during the frame after. Measuring in the same tick reads a geometry
  // that is about to change, and the correction is computed against it.
  let frame = 0;
  const settleNextFrame = (): void => {
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(done);
    });
  };

  // Read into a boolean first: `if ("onscrollend" in window)` narrows `window`
  // itself, and the else branch then types as `never`.
  const hasScrollEnd = "onscrollend" in window;
  if (hasScrollEnd) {
    window.addEventListener("scrollend", settleNextFrame, { once: true });
    return () => {
      window.removeEventListener("scrollend", settleNextFrame);
      cancelAnimationFrame(frame);
    };
  }
  const timer = window.setTimeout(settleNextFrame, SCROLL_SETTLE_FALLBACK_MS);
  return () => {
    window.clearTimeout(timer);
    cancelAnimationFrame(frame);
  };
}

/** Where the page must sit for `section` to be at the marker, measured now. */
function targetScrollFor(
  section: HTMLElement,
  nav: HTMLElement | null,
): number {
  return (
    section.getBoundingClientRect().top + window.scrollY - markerOffset(nav)
  );
}

/** True when the page is already where a scroll would put it.
 *
 *  `scrollTo` fires no `scrollend` if it does not move — and it does not move
 *  when the reader clicks the tab for the section they are already on, or the
 *  last tab while already at the bottom, where the browser clamps the target to
 *  the current offset. Anything waiting on that event then waits for the
 *  backstop instead. One pixel of slack because scroll offsets are fractional
 *  on scaled displays. */
function alreadyAt(top: number): boolean {
  return Math.abs(window.scrollY - top) <= 1;
}

/**
 * The ids `forceSectionsLaidOut` actually needs to land on `targetId`: every
 * tab's section AT OR BEFORE the one that owns it, in document order — not
 * every tab on the page.
 *
 * `targetScrollFor` reads only the target's OWN measured position plus the
 * CURRENT `window.scrollY`; nothing below the target in the document can
 * change that number, only content ABOVE it can. Forcing sections below the
 * target too — which `forceSectionsLaidOut` used to always do, over the whole
 * tab list — meant every ordinary tab click forced a full-document relayout,
 * not just the page-load deep link this mechanism was built for.
 *
 * `targetId` is usually a tab's own section id (always true for a tab click or
 * the deep-link mount effect); `onDocumentClick` also reaches arbitrary
 * in-page targets NESTED inside a tab's section (an FAQ panel, one review), so
 * this walks tabs in document order and stops at the one whose section
 * contains the target, rather than assuming `targetId` names a tab directly.
 *
 * Falls back to every tab when the target isn't found inside any of them —
 * the old, broader behaviour, and a safe default for a case this page should
 * never actually produce.
 */
function sectionIdsThrough(
  tabs: readonly TourTab[],
  targetId: string,
): readonly string[] {
  const target = document.getElementById(targetId);
  const ids: string[] = [];
  for (const tab of tabs) {
    const section = document.getElementById(tab.slug);
    ids.push(tab.slug);
    if (section && target && (section === target || section.contains(target))) {
      return ids;
    }
  }
  return tabs.map((tab) => tab.slug);
}

/**
 * Make every section in `ids` report its REAL height, and return a function
 * that puts each one back exactly how it was.
 *
 * This is the actual fix for landing drift, not the correction loop below —
 * that loop can only ever react to a wrong measurement after the fact, and
 * how many reactions it takes depends on how many sections are still
 * unresolved, which has no fixed bound. This removes the unknown instead:
 * `content-visibility: auto` is what substitutes a section's CSS
 * `contain-intrinsic-size` placeholder for its real size while the browser
 * doesn't consider it worth laying out. Of the built-in tab sections, only the
 * long below-fold ones opt into it (`#included` and `#reviews` in
 * `_static-sections.tsx`, the itinerary, any custom tab) — the rest
 * (`overview`, `dates`, `faqs`, `maps`) don't, so forcing one of those is a
 * harmless no-op.  Forcing it to `visible` makes the browser lay the section
 * out for real, so the very next measurement — not the third or eighth — is
 * the true one.
 */
export function forceSectionsLaidOut(ids: readonly string[]): () => void {
  const sections = ids
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el != null);
  // Capture whatever was already inline (ordinarily nothing — the `auto`
  // comes from a stylesheet rule) so releasing restores exactly that, rather
  // than assuming every section's resting state is `auto`.
  const previous = sections.map((el) => el.style.contentVisibility);
  sections.forEach((el) => {
    el.style.contentVisibility = "visible";
  });
  return () => {
    sections.forEach((el, i) => {
      el.style.contentVisibility = previous[i] ?? "";
    });
  };
}

/**
 * Put `slug` on the marker and keep it there once the page stops moving.
 *
 * `tabs` lets `forceSectionsLaidOut` resolve only the sections AT OR BEFORE
 * the target (`sectionIdsThrough`) so the FIRST measurement below is already
 * exact, without forcing a relayout of the whole page on every ordinary tab
 * click. The correction loop that follows is a backstop for whatever that
 * cannot anticipate (a widget that resizes after its own data arrives, say),
 * not the mechanism landing drift actually relied on — see
 * `MAX_LANDING_CORRECTIONS`'s own comment for why that distinction matters.
 *
 * The forced layout stays in place until the landing is FINAL (`finish`),
 * not released the moment the first scroll is issued: a section still
 * travelling past mid-animation is free to spring back to its estimate the
 * instant `auto` resumes, which would shrink the page out from under a scroll
 * that is still moving toward a target computed against the larger, resolved
 * size.
 *
 * `onSettled` runs after the landing is final, so the scrollspy re-reads live
 * geometry rather than staying on whatever the click asserted.
 */
export function landOnSection(
  slug: string,
  navRef: RefObject<HTMLElement | null>,
  onSettled: () => void,
  tabs: readonly TourTab[],
): () => void {
  const section = document.getElementById(slug);
  if (!section) {
    onSettled();
    return () => {};
  }
  const reduce =
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  let releaseLayout = forceSectionsLaidOut(sectionIdsThrough(tabs, slug));
  let top = targetScrollFor(section, navRef.current);
  // The sections through the target are the only ones `targetScrollFor`
  // depends on — but reaching `top` at all also needs the PAGE to be tall
  // enough, and sections AFTER the target are still placeholder-sized at this
  // point. Rare: it only bites when the target sits close to the end of an
  // otherwise-short page, with large unresolved sections still after it. The
  // correction loop below cannot fix this case on its own — `top` is already
  // correct, it is merely unreachable — so it is cheaper to rule out here
  // than to let three correction passes fail against the same clamp.
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  if (top > maxScroll + 1) {
    releaseLayout();
    releaseLayout = forceSectionsLaidOut(tabs.map((tab) => tab.slug));
    top = targetScrollFor(section, navRef.current);
  }

  const finish = (): void => {
    releaseLayout();
    onSettled();
  };

  let cancelSettle: () => void = () => {};
  let correctionsLeft = MAX_LANDING_CORRECTIONS;
  let lastDrift = Infinity;

  const settle = (): void => {
    const drift =
      section.getBoundingClientRect().top - markerOffset(navRef.current);
    if (shouldCorrectLanding({ drift, correctionsLeft, lastDrift })) {
      correctionsLeft -= 1;
      lastDrift = Math.abs(drift);
      const correctedTop = targetScrollFor(section, navRef.current);
      // A correction that would not move the page produces no `scrollend`
      // either, so re-settle directly rather than waiting for one.
      if (alreadyAt(correctedTop)) {
        finish();
        return;
      }
      cancelSettle = whenScrollSettles(settle);
      window.scrollTo({ top: correctedTop, behavior: "auto" });
      return;
    }
    finish();
  };

  // Nothing to wait for when the page is already there. Registering the settle
  // listener anyway left `onSettled` uncalled, so the scrollspy stayed locked
  // for the full backstop — six seconds during which scrolling away did not
  // update the active tab, on the ordinary action of clicking the tab you are
  // already reading.
  if (alreadyAt(top)) {
    finish();
    return () => {};
  }

  cancelSettle = whenScrollSettles(settle);
  window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
  return () => {
    cancelSettle();
    releaseLayout();
  };
}
